import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Channel, Conversation, Message } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { isEvent, useChannel } from "./realtime";
import { mergeMessages, typingActive, typingOff, typingOn, type GuardReason, type TypingState } from "../rooms/model";

/**
 * P3 聊天室数据：房间列表 / 详情走 react-query；消息流走本地 reducer（分页 + conversation:<cid> 实时合并，按 id 去重）。
 * 契约里没有 conversation.updated：护栏 / 成员事件直接改详情缓存，agent 发言后重新拉详情（刷新已用预算）。
 */

export const rk = {
  rooms: (pid: string) => ["rooms", pid] as const,
  conv: (cid: string) => ["conversation", cid] as const,
};

export function useRooms(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: rk.rooms(pid ?? ""),
    enabled: !!pid,
    queryFn: async () => (await api.call("conversationList", { params: { pid: pid! }, query: { kind: "room", limit: 100 } })).items,
  });
}

export function useConversation(cid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: rk.conv(cid ?? ""), enabled: !!cid, queryFn: () => api.call("conversationGet", { params: { cid: cid! } }) });
}

/** 写回一个会话（详情 + 列表） */
export function putConversation(qc: QueryClient, c: Conversation) {
  qc.setQueryData(rk.conv(c.id), c);
  qc.setQueryData<Conversation[]>(rk.rooms(c.projectId), (xs) => {
    if (!xs || c.kind !== "room") return xs;
    return xs.some((x) => x.id === c.id) ? xs.map((x) => (x.id === c.id ? c : x)) : [c, ...xs];
  });
}

interface MsgState {
  items: Message[];
  cursor: string | null;
  loaded: boolean;
  loadingOlder: boolean;
}

type MsgAction =
  | { type: "reset" }
  | { type: "page"; items: Message[]; cursor: string | null; first: boolean }
  | { type: "upsert"; msg: Message }
  | { type: "older" };

function msgReducer(s: MsgState, a: MsgAction): MsgState {
  switch (a.type) {
    case "reset":
      return { items: [], cursor: null, loaded: false, loadingOlder: false };
    case "page":
      return { items: mergeMessages(s.items, a.items), cursor: a.first && s.loaded ? s.cursor : a.cursor, loaded: true, loadingOlder: false };
    case "upsert":
      return { ...s, items: mergeMessages(s.items, [a.msg]) };
    case "older":
      return { ...s, loadingOlder: true };
  }
}

const PAGE = 50;

/**
 * 房间实时状态：消息（分页 + 实时）、typing、最近一次护栏暂停原因。
 * 先订阅通道再拉第一页；两边按 id 合并，顺序无关。gap 时重新拉第一页。
 */
export function useRoomStream(cid: string | undefined) {
  const { api } = useServices();
  const qc = useQueryClient();
  const [msgs, dispatch] = useReducer(msgReducer, { items: [], cursor: null, loaded: false, loadingOlder: false });
  const [typing, setTyping] = useState<TypingState>({});
  const [pausedReason, setPausedReason] = useState<GuardReason | null>(null);
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const alive = useRef(true);

  const loadFirst = useCallback(async () => {
    if (!cid) return;
    try {
      const page = await api.call("messageList", { params: { cid }, query: { limit: PAGE } });
      if (alive.current) dispatch({ type: "page", items: page.items, cursor: page.nextCursor, first: true });
    } catch {
      if (alive.current) dispatch({ type: "page", items: [], cursor: null, first: true });
    }
  }, [api, cid]);

  useEffect(() => {
    alive.current = true;
    dispatch({ type: "reset" });
    setTyping({});
    setPausedReason(null);
    void loadFirst();
    return () => {
      alive.current = false;
    };
  }, [loadFirst]);

  // typing 过期后重新渲染
  const active = typingActive(typing);
  useEffect(() => {
    if (!active.length) return;
    const next = Math.min(...active.map((id) => typing[id]!)) - Date.now();
    const h = setTimeout(tick, Math.max(50, next + 20));
    return () => clearTimeout(h);
  }, [typing, active]);

  const patchConv = (fn: (c: Conversation) => Conversation) => {
    const cur = cid ? qc.getQueryData<Conversation>(rk.conv(cid)) : undefined;
    if (cur) putConversation(qc, fn(cur));
  };

  useChannel(cid ? (`conversation:${cid}` as Channel) : null, {
    onEvent(e) {
      if (isEvent(e, "message.created") || isEvent(e, "message.updated")) {
        dispatch({ type: "upsert", msg: e.data });
        if (e.data.from.type === "agent") {
          setTyping((s) => typingOff(s, (e.data.from as { id: string }).id));
          void qc.invalidateQueries({ queryKey: rk.conv(cid!) });
        }
      } else if (isEvent(e, "typing")) setTyping((s) => typingOn(s, e.data.agentId));
      else if (isEvent(e, "guard.paused")) {
        setPausedReason(e.data.reason);
        setTyping({});
        patchConv((c) => ({ ...c, paused: true }));
        void qc.invalidateQueries({ queryKey: rk.conv(cid!) });
      } else if (isEvent(e, "guard.resumed")) {
        setPausedReason(null);
        patchConv((c) => ({ ...c, paused: false }));
      } else if (isEvent(e, "member.changed")) patchConv((c) => ({ ...c, members: e.data.members }));
    },
    onGap() {
      void loadFirst();
      void qc.invalidateQueries({ queryKey: rk.conv(cid!) });
    },
  });

  const loadOlder = useCallback(async () => {
    if (!cid || !msgs.cursor || msgs.loadingOlder) return;
    dispatch({ type: "older" });
    const page = await api.call("messageList", { params: { cid }, query: { limit: PAGE, cursor: msgs.cursor } });
    if (alive.current) dispatch({ type: "page", items: page.items, cursor: page.nextCursor, first: false });
  }, [api, cid, msgs.cursor, msgs.loadingOlder]);

  return {
    messages: msgs.items,
    loaded: msgs.loaded,
    hasOlder: !!msgs.cursor,
    loadingOlder: msgs.loadingOlder,
    loadOlder,
    typing: active,
    pausedReason,
    setPausedReason,
    /** 自己发出的消息立即并入（不等 WS 回声） */
    addLocal: (m: Message) => dispatch({ type: "upsert", msg: m }),
  };
}
